import React, { Suspense, lazy, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Download, RamenDining, TabletAndroidRounded } from "@mui/icons-material";
import { AuthProvider } from "./contexts/AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { CustomThemeProvider } from "./contexts/ThemeContext";
import { RestaurantProvider } from "./contexts/RestaurantContext";
import { Box, CircularProgress, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Login from "./pages/Login";
import Layout from "./components/Layout/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import SuperUserProtectedRoute from "./components/SuperUserProtectedRoute";
import HomePage from "./pages/user/HomePage";
import RestaurantConfig from "./components/admin/RestaurantConfig";
import { useNavigationGuard } from "./hooks/useNavigationGuard";
import CheckoutPage from "./pages/user/CheckoutPage";
import PaymentResultPage from "./pages/user/PaymentResultPage";
import OrderSuccessPage from "./pages/user/OrderSuccessPage";
import TableOrderManagement from "./components/admin/TableOrderManagement";
import GuestSignupPage from "./pages/user/GuestSignupPage";
import TokenManagement from "./pages/TokenManagement";
import TableOrderPage from "./pages/user/TableOrderPage";
import TableMenuPage from "./pages/user/TableMenuPage";
import TableCheckoutPage from "./pages/user/TableCheckoutPage";
import TestimonialManagement from "./pages/TestimonialManagement";
import TableAdditionManagement from "./pages/admin/TableAdditionManagement";
import MenuPerformanceReportManagement from "./pages/MenuPerformanceReportManagement";
import GSTReportManagement from "./pages/GSTReportManagement";
import PeakHoursItemsManagement from "./pages/PeakHoursItemsManagement";
import PeakHoursOrdersManagement from "./pages/PeakHoursOrdersManagement";
import FeedbackManagement from "./pages/FeedBackManagement";
import TestPage from "./pages/user/TestPage";
// import TermsPage from "./pages/user/TermsPage";
import PrivacyPage from "./pages/user/PrivacyPage";
import AboutPage from "./pages/user/AboutPage";
import ContactPage from "./pages/user/ContactPage";
import UserSettingsManagment from "./pages/UserSettingsManagment";
// import Quotation from './components/admin/Quotation';
import SocialLinksConfig from "./components/admin/SocialLinksConfig";
import SkeletonLoader, {
  DashboardSkeleton,
  PageSkeleton,
  CheckoutPageSkeleton,
  MenuItemSkeleton,
  DeliveryPageSkeleton,
  HomePageSkeleton,
  TableReservationsSkeleton
} from "./components/common/SkeletonLoader";
import OrderDetailsPage from "./pages/user/OrderDetailsPage";
import TableOrderCheckout from "./pages/user/TableOrderCheckout"
import ProfilePage from "./components/user/ProfilePage";
import UserManagement from "./pages/UserManagement";
import UserNavbar from "./components/user/UserNavbar";
import Header from "./components/Layout/Header";
import Quotation from "./components/admin/Quotation";
import QuotationComponent from "./components/admin/QuotationComponent";
import CateringInvoiceComponent from "./components/admin/CateringInvoiceComponent";
import HeroSection from "./pages/user/HeroSection";
import { generateToken, messaging } from "./notification/Firebase";
import { onMessage } from "firebase/messaging";
import { toast } from "react-toastify";
import StyledToastContainer from "./components/common/StyledToastContainer";
import NotificationManagement from "./pages/NotificationManagement";
import { get_notifications_count } from "./services/notificationService";
import SalesReportManagement from "./pages/SalesReportManagement";
import OrderReportManagement from "./pages/OrderReportManagement";
import PrinterSettingsManagement from "./pages/PrinterSettingsManagement";
import { PDFProvider } from './components/PDFProvider';
import PrivacyPolicyPage from "./pages/user/PrivacyPolicyPage";
import PeakHoursItems from "./pages/PeakHoursItemsManagement";




// Lazy load admin components
const Dashboard = lazy(() => import("./pages/Dashboard"));
const OutletManagement = lazy(() => import("./pages/OutletManagement"));
const MenuManagement = lazy(() => import("./pages/MenuManagement"));
const DeliveryManagement = lazy(() => import("./pages/DeliveryManagement"));
const CateringManagement = lazy(() => import("./pages/CateringManagement"));
const CateringInfoManagement = lazy(() =>
  import("./pages/CateringInfoManagement")
);

// Lazy load user pages
const DeliveryPage2 = lazy(() => import("./pages/user/DeliveryPage2"));
const TakeawayPage = lazy(() => import("./pages/user/TakeawayPage"));
const CateringPage = lazy(() => import("./pages/user/CateringPage"));
const PublicRestaurant = lazy(() => import("./pages/PublicRestaurant"));
const TableReservationsPage = lazy(() =>
  import("./pages/user/TableReservationsPage")
);
const TableManagement = lazy(() => import("./pages/admin/TableManagement"));

// Loading component for suspense fallback
const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
    sx={{
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "white",
    }}
  >
    <Box textAlign="center">
      <CircularProgress size={60} sx={{ color: "white", mb: 2 }} />
      <Typography variant="h6">Loading...</Typography>
    </Box>
  </Box>
);

// Page wrapper with Suspense
const PageWrapper = ({ children, fallback }) => (
  <Suspense fallback={fallback || <LoadingFallback />}>{children}</Suspense>
);

// Navigation guard wrapper component
const NavigationGuardWrapper = ({ children }) => {
  useNavigationGuard();
  return children;
};

function App() {

  const [notificationCount, setNotificationCount] = useState(0)
  const [deliveryNotificationCount, setDeliveryNotificationCount] = useState(0)
  const [takeawayNotificationCount, setTakeawayNotificationCount] = useState(0)
  const [cateringNotificationCount, setCateringNotificationCount] = useState(0)
  const [reservationNotificationCount, setReservationNotificationCount] = useState(0)
  const [tableIdForCloseBill,setTableIdForCloseBill] = useState(0);


  async function getNotificationCount() {
    try {
      const response = await get_notifications_count()
      // console.log(response)
      let value = response.unread
      let delivery = response.delivery_unread
      let takeaway = response.takeaway_unread
      let catering = response.catering_unread
      let reservation = response.reservation_unread
      // console.log(value)
      setNotificationCount(value)
      setDeliveryNotificationCount(delivery)
      setTakeawayNotificationCount(takeaway)
      setCateringNotificationCount(catering)
      setReservationNotificationCount(reservation)

      // console.log("Yes From App => ", catering)

    }
    catch (error) {
      console.error(error)
    }
  }

  const playNotificationSound = () => {
    const audio = new Audio("/notification.mp3");
    audio.volume = 0.6;
    audio.play().catch(() => {
      // Autoplay blocked until user interacts
    });
  };

  // useEffect(() => {
  //   const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
  //     navigator.userAgent
  //   );

  //   console.log('Device:', isMobile ? 'Mobile' : 'Desktop');

  //   // Initialize notifications
  //   const initializeNotifications = async () => {
  //     try {
  //       // Check if service worker is supported
  //       if ('serviceWorker' in navigator) {
  //         // Register service worker
  //         const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  //         console.log('Service Worker registered:', registration);

  //         // Wait for service worker to be ready
  //         await navigator.serviceWorker.ready;
  //         console.log('Service Worker ready');
  //       }

  //       // Generate FCM token
  //       await generateToken();

  //       // Request notification permission if not granted
  //       if (Notification.permission !== 'granted') {
  //         const permission = await Notification.requestPermission();
  //         console.log('Notification permission:', permission);
  //       }
  //     } catch (error) {
  //       console.error('Notification initialization failed:', error);
  //     }
  //   };

  //   initializeNotifications();

  //   // Track app visibility
  //   const handleVisibilityChange = () => {
  //     console.log('App state changed to:', document.visibilityState);
  //   };
  //   document.addEventListener('visibilitychange', handleVisibilityChange);

  //   // FOREGROUND message handler (app is open and visible)
  //   const unsubscribe = onMessage(messaging, (payload) => {
  //     console.log("✅ Foreground message received:", payload);

  //     let title = payload?.notification?.title || payload?.data?.title || "New Notification";
  //     let body = payload?.notification?.body || payload?.data?.body || "";

  //     console.log("Notification details:", { title, body });

  //     const isAdminRoute = window.location.pathname.startsWith('/admin');

  //     if (isAdminRoute) {
  //       // Play sound/vibrate for all notifications when in foreground
  //       if (isMobile && navigator.vibrate) {
  //         navigator.vibrate(200); // Vibrate on mobile
  //       } else {
  //         playNotificationSound(); // Play sound on desktop
  //       }

  //       // Show in-app toast
  //       showNotificationToast(title, body, isMobile);

  //       // Update notification count
  //       getNotificationCount();
  //     }
  //   });

  //   // Toast function (same as yours)
  //   const showNotificationToast = (title, body, isMobile) => {
  //     const toastId = `notification-${Date.now()}`;

  //     toast(
  //       <Box
  //         sx={{
  //           height: isMobile ? '85px' : '75px',
  //           backgroundColor: '#1565c0',
  //           display: "flex",
  //           gap: isMobile ? 2 : 1,
  //           alignItems: "center",
  //           width: "100%",
  //           p: isMobile ? 2 : 1.5,
  //           borderRadius: isMobile ? '16px' : '8px',
  //           boxShadow: isMobile ? '0 8px 16px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
  //           cursor: 'pointer',
  //           WebkitTapHighlightColor: 'transparent',
  //           touchAction: 'manipulation', // Better touch handling
  //         }}
  //         onClick={() => {
  //           // Handle navigation or action
  //           console.log('Toast tapped');
  //           toast.dismiss(toastId);
  //         }}
  //       >
  //         <RamenDining sx={{
  //           color: "#fff",
  //           fontSize: isMobile ? "28px" : "16px",
  //           ml: 1
  //         }} />
  //         <Box sx={{
  //           display: "flex",
  //           flexDirection: "column",
  //           gap: 0.5,
  //           flex: 1,
  //         }}>
  //           <Typography
  //             sx={{
  //               fontFamily: "Inter",
  //               fontSize: isMobile ? "16px" : "12px",
  //               color: "#fff",
  //               fontWeight: "600",
  //               lineHeight: 1.2,
  //               pr: 2
  //             }}
  //           >
  //             {title}
  //           </Typography>
  //           <Typography
  //             sx={{
  //               fontFamily: "Inter",
  //               fontSize: isMobile ? "14px" : "10px",
  //               color: "#fff",
  //               fontWeight: "400",
  //               lineHeight: 1.3,
  //               pr: 2
  //             }}
  //           >
  //             {body}
  //           </Typography>
  //         </Box>
  //       </Box>,
  //       {
  //         autoClose: isMobile ? 4000 : 5000,
  //         closeOnClick: true,
  //         pauseOnHover: false,
  //         pauseOnFocusLoss: false,
  //         draggable: isMobile,
  //         hideProgressBar: true,
  //         toastId: toastId,
  //         position: isMobile ? "top-center" : "bottom-right",
  //         style: {
  //           background: "#1565c0",
  //           borderRadius: isMobile ? "16px" : "8px",
  //           boxShadow: isMobile ? "0 8px 16px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.08)",
  //           padding: "0",
  //           margin: isMobile ? "8px" : "12px",
  //           color: '#fff',
  //           width: isMobile ? 'calc(100% - 16px)' : 'auto',
  //           maxWidth: isMobile ? '400px' : '300px',
  //           left: isMobile ? '8px' : 'auto',
  //           right: isMobile ? '8px' : 'auto',
  //           top: isMobile ? '16px' : 'auto',
  //         }
  //       }
  //     );
  //   };

  //   return () => {
  //     unsubscribe();
  //     document.removeEventListener('visibilitychange', handleVisibilityChange);
  //   };
  // }, []);


  // useEffect(() => {
  //   const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
  //     navigator.userAgent
  //   );

  //   console.log('Device:', isMobile ? 'Mobile' : 'Desktop');

  //   // Initialize notifications
  //   const initializeNotifications = async () => {
  //     try {
  //       if ('serviceWorker' in navigator) {
  //         const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  //         console.log('Service Worker registered:', registration);
  //         await navigator.serviceWorker.ready;
  //         console.log('Service Worker ready');
  //       }

  //       await generateToken();

  //       if (Notification.permission !== 'granted') {
  //         const permission = await Notification.requestPermission();
  //         console.log('Notification permission:', permission);
  //       }
  //     } catch (error) {
  //       console.error('Notification initialization failed:', error);
  //     }
  //   };

  //   initializeNotifications();

  //   // ✅ Listen for postMessage from Service Worker (background notification click)
  //   const handleServiceWorkerMessage = (event) => {
  //     console.log('[App] Message from SW:', event.data);

  //     if (event.data?.type === 'NOTIFICATION_CLICK') {
  //       const url = event.data.url;
  //       console.log('[App] Redirecting to:', url);

  //       // Extract pathname from full URL
  //       const path = new URL(url).pathname;

  //       // If using React Router
  //       // navigate(path);

  //       // If NOT using React Router
  //       window.location.href = url;
  //     }
  //   };

  //   // ✅ Register the SW message listener
  //   if ('serviceWorker' in navigator) {
  //     navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
  //   }

  //   // Track app visibility
  //   const handleVisibilityChange = () => {
  //     console.log('App state changed to:', document.visibilityState);
  //   };
  //   document.addEventListener('visibilitychange', handleVisibilityChange);

  //   // FOREGROUND message handler
  //   const unsubscribe = onMessage(messaging, (payload) => {
  //     console.log("✅ Foreground message received:", payload);

  //     let title = payload?.notification?.title || payload?.data?.title || "New Notification";
  //     let body = payload?.notification?.body || payload?.data?.body || "";

  //     const isAdminRoute = window.location.pathname.startsWith('/admin');

  //     if (isAdminRoute) {
  //       if (isMobile && navigator.vibrate) {
  //         navigator.vibrate(200);
  //       } else {
  //         playNotificationSound();
  //       }

  //       showNotificationToast(title, body, isMobile);
  //       getNotificationCount();
  //     }
  //   });

  //   const showNotificationToast = (title, body, isMobile) => {
  //     const toastId = `notification-${Date.now()}`;

  //     toast(
  //       <Box
  //         sx={{
  //           height: isMobile ? '85px' : '75px',
  //           backgroundColor: '#1565c0',
  //           display: "flex",
  //           gap: isMobile ? 2 : 1,
  //           alignItems: "center",
  //           width: "100%",
  //           p: isMobile ? 2 : 1.5,
  //           borderRadius: isMobile ? '16px' : '8px',
  //           boxShadow: isMobile ? '0 8px 16px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
  //           cursor: 'pointer',
  //           WebkitTapHighlightColor: 'transparent',
  //           touchAction: 'manipulation',
  //         }}
  //         onClick={() => {
  //           console.log('Toast tapped');
  //           toast.dismiss(toastId);
  //         }}
  //       >
  //         <RamenDining sx={{ color: "#fff", fontSize: isMobile ? "28px" : "16px", ml: 1 }} />
  //         <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
  //           <Typography sx={{ fontFamily: "Inter", fontSize: isMobile ? "16px" : "12px", color: "#fff", fontWeight: "600", lineHeight: 1.2, pr: 2 }}>
  //             {title}
  //           </Typography>
  //           <Typography sx={{ fontFamily: "Inter", fontSize: isMobile ? "14px" : "10px", color: "#fff", fontWeight: "400", lineHeight: 1.3, pr: 2 }}>
  //             {body}
  //           </Typography>
  //         </Box>
  //       </Box>,
  //       {
  //         autoClose: isMobile ? 4000 : 5000,
  //         closeOnClick: true,
  //         pauseOnHover: false,
  //         pauseOnFocusLoss: false,
  //         draggable: isMobile,
  //         hideProgressBar: true,
  //         toastId: toastId,
  //         position: isMobile ? "top-center" : "bottom-right",
  //         style: {
  //           background: "#1565c0",
  //           borderRadius: isMobile ? "16px" : "8px",
  //           boxShadow: isMobile ? "0 8px 16px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.08)",
  //           padding: "0",
  //           margin: isMobile ? "8px" : "12px",
  //           color: '#fff',
  //           width: isMobile ? 'calc(100% - 16px)' : 'auto',
  //           maxWidth: isMobile ? '400px' : '300px',
  //           left: isMobile ? '8px' : 'auto',
  //           right: isMobile ? '8px' : 'auto',
  //           top: isMobile ? '16px' : 'auto',
  //         }
  //       }
  //     );
  //   };

  //   // ✅ Cleanup all listeners
  //   return () => {
  //     unsubscribe();
  //     document.removeEventListener('visibilitychange', handleVisibilityChange);
  //     if ('serviceWorker' in navigator) {
  //       navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
  //     }
  //   };
  // }, []);

  // useEffect(() => {
  //   const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
  //     navigator.userAgent
  //   );

  //   // console.log('Device:', isMobile ? 'Mobile' : 'Desktop');

  //   // ✅ Check if current route is admin BEFORE initializing notifications
  //   const isAdminRoute = window.location.pathname.startsWith('/admin');

  //   // Initialize notifications ONLY for admin users
  //   const initializeNotifications = async () => {
  //     try {
  //       // Only proceed if user is on admin route
  //       if (!isAdminRoute) {
  //         // console.log('Non-admin user - skipping notification registration');
  //         return;
  //       }

  //       // console.log('Admin user - initializing notifications');

  //       if ('serviceWorker' in navigator) {
  //         const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  //         // console.log('Service Worker registered:', registration);
  //         await navigator.serviceWorker.ready;
  //         // console.log('Service Worker ready');
  //       }

  //       await generateToken();

  //       if (Notification.permission !== 'granted') {
  //         const permission = await Notification.requestPermission();
  //         // console.log('Notification permission:', permission);
  //       }
  //     } catch (error) {
  //       // console.error('Notification initialization failed:', error);
  //     }
  //   };

  //   initializeNotifications();

  //   // ✅ Listen for postMessage from Service Worker (background notification click)
  //   const handleServiceWorkerMessage = (event) => {
  //     // console.log('[App] Message from SW:', event.data);

  //     if (event.data?.type === 'NOTIFICATION_CLICK') {
  //       const url = event.data.url;
  //       // console.log('[App] Redirecting to:', url);

  //       // Extract pathname from full URL
  //       const path = new URL(url).pathname;

  //       // If using React Router
  //       // navigate(path);

  //       // If NOT using React Router
  //       window.location.href = url;
  //     }
  //   };

  //   // ✅ Register the SW message listener
  //   if ('serviceWorker' in navigator) {
  //     navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
  //   }

  //   // Track app visibility
  //   const handleVisibilityChange = () => {
  //     // console.log('App state changed to:', document.visibilityState);
  //   };
  //   document.addEventListener('visibilitychange', handleVisibilityChange);

  //   // FOREGROUND message handler
  //   const unsubscribe = onMessage(messaging, (payload) => {
  //     // console.log("✅ Foreground message received:", payload);

  //     let title = payload?.notification?.title || payload?.data?.title || "New Notification";
  //     let body = payload?.notification?.body || payload?.data?.body || "";

  //     // Check if current route is admin
  //     if (window.location.pathname.startsWith('/admin')) {
  //       if (isMobile && navigator.vibrate) {
  //         navigator.vibrate(200);
  //       } else {
  //         playNotificationSound();
  //       }

  //       showNotificationToast(title, body, isMobile);
  //       getNotificationCount();
  //     }
  //   });

  //   const showNotificationToast = (title, body, isMobile) => {
  //     const toastId = `notification-${Date.now()}`;

  //     toast(
  //       <Box
  //         sx={{
  //           height: isMobile ? '85px' : '75px',
  //           backgroundColor: '#1565c0',
  //           display: "flex",
  //           gap: isMobile ? 2 : 1,
  //           alignItems: "center",
  //           width: "100%",
  //           p: isMobile ? 2 : 1.5,
  //           borderRadius: isMobile ? '16px' : '8px',
  //           boxShadow: isMobile ? '0 8px 16px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
  //           cursor: 'pointer',
  //           WebkitTapHighlightColor: 'transparent',
  //           touchAction: 'manipulation',
  //         }}
  //         onClick={() => {
  //           // console.log('Toast tapped');
  //           toast.dismiss(toastId);
  //         }}
  //       >
  //         <RamenDining sx={{ color: "#fff", fontSize: isMobile ? "28px" : "16px", ml: 1 }} />
  //         <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
  //           <Typography sx={{ fontFamily: "Inter", fontSize: isMobile ? "16px" : "12px", color: "#fff", fontWeight: "600", lineHeight: 1.2, pr: 2 }}>
  //             {title}
  //           </Typography>
  //           <Typography sx={{ fontFamily: "Inter", fontSize: isMobile ? "14px" : "10px", color: "#fff", fontWeight: "400", lineHeight: 1.3, pr: 2 }}>
  //             {body}
  //           </Typography>
  //         </Box>
  //       </Box>,
  //       {
  //         autoClose: isMobile ? 4000 : 5000,
  //         closeOnClick: true,
  //         pauseOnHover: false,
  //         pauseOnFocusLoss: false,
  //         draggable: isMobile,
  //         hideProgressBar: true,
  //         toastId: toastId,
  //         position: isMobile ? "top-center" : "bottom-right",
  //         style: {
  //           background: "#1565c0",
  //           borderRadius: isMobile ? "16px" : "8px",
  //           boxShadow: isMobile ? "0 8px 16px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.08)",
  //           padding: "0",
  //           margin: isMobile ? "8px" : "12px",
  //           color: '#fff',
  //           width: isMobile ? 'calc(100% - 16px)' : 'auto',
  //           maxWidth: isMobile ? '400px' : '300px',
  //           left: isMobile ? '8px' : 'auto',
  //           right: isMobile ? '8px' : 'auto',
  //           top: isMobile ? '16px' : 'auto',
  //         }
  //       }
  //     );
  //   };

  //   // ✅ Cleanup all listeners
  //   return () => {
  //     unsubscribe();
  //     document.removeEventListener('visibilitychange', handleVisibilityChange);
  //     if ('serviceWorker' in navigator) {
  //       navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
  //     }
  //   };
  // }, []);

  //   import { messaging, setupForegroundNotifications, generateToken } from '../firebase'; // Adjust path
  // import { onMessage } from 'firebase/messaging'; // Import onMessage

  useEffect(() => {
    let unsubscribe = null; // Store unsubscribe function

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

    const isAdminRoute = window.location.pathname.startsWith('/admin');

    const initializeNotifications = async () => {
      try {
        if (!isAdminRoute) {
          return;
        }

        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
          await navigator.serviceWorker.ready;
        }

        await generateToken();

        if (Notification.permission !== 'granted') {
          await Notification.requestPermission();
        }
      } catch (error) {
        console.error('Notification initialization failed:', error);
      }
    };

    initializeNotifications();

    // Handle Service Worker messages (background clicks)
    const handleServiceWorkerMessage = (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICK') {
        const url = event.data.url;
        const path = new URL(url).pathname;
        window.location.href = url;
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    // Setup foreground notifications
    const setupForegroundHandler = async () => {
      try {
        const messagingInstance = await messaging.getInstance();
        if (messagingInstance && isAdminRoute) {
          // Use the actual messaging instance
          unsubscribe = onMessage(messagingInstance, (payload) => {
            console.log("✅ Foreground message received:", payload);

            if (payload.data?.type === "DINE_IN") {
              console.log("Live Order Update:", payload.data);
              console.log("Live Order Update:", payload?.data?.table_id)
              setTableIdForCloseBill(payload.data?.table_id)

              // Refresh your order list
              // fetchOrders(); // or getOrders(), loadOrders(), etc.

              return;
            }

            let title = payload?.notification?.title || payload?.data?.title || "New Notification";
            let body = payload?.notification?.body || payload?.data?.body || "";

            if (window.location.pathname.startsWith('/admin')) {
              if (isMobile && navigator.vibrate) {
                navigator.vibrate(200);
              } else {
                playNotificationSound();
              }

              showNotificationToast(title, body, isMobile);
              getNotificationCount();
            }
          });
        }
      } catch (error) {
        console.error('Failed to setup foreground handler:', error);
      }
    };

    setupForegroundHandler();

    const showNotificationToast = (title, body, isMobile) => {
      const toastId = `notification-${Date.now()}`;

      toast(
        <Box
          sx={{
            height: isMobile ? '85px' : '75px',
            backgroundColor: '#1565c0',
            display: "flex",
            gap: isMobile ? 2 : 1,
            alignItems: "center",
            width: "100%",
            p: isMobile ? 2 : 1.5,
            borderRadius: isMobile ? '16px' : '8px',
            boxShadow: isMobile ? '0 8px 16px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation',
          }}
          onClick={() => {
            toast.dismiss(toastId);
          }}
        >
          <RamenDining sx={{ color: "#fff", fontSize: isMobile ? "28px" : "16px", ml: 1 }} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
            <Typography sx={{ fontFamily: "Inter", fontSize: isMobile ? "16px" : "12px", color: "#fff", fontWeight: "600", lineHeight: 1.2, pr: 2 }}>
              {title}
            </Typography>
            <Typography sx={{ fontFamily: "Inter", fontSize: isMobile ? "14px" : "10px", color: "#fff", fontWeight: "400", lineHeight: 1.3, pr: 2 }}>
              {body}
            </Typography>
          </Box>
        </Box>,
        {
          autoClose: isMobile ? 4000 : 5000,
          closeOnClick: true,
          pauseOnHover: false,
          pauseOnFocusLoss: false,
          draggable: isMobile,
          hideProgressBar: true,
          toastId: toastId,
          position: isMobile ? "top-center" : "bottom-right",
          style: {
            background: "#1565c0",
            borderRadius: isMobile ? "16px" : "8px",
            boxShadow: isMobile ? "0 8px 16px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.08)",
            padding: "0",
            margin: isMobile ? "8px" : "12px",
            color: '#fff',
            width: isMobile ? 'calc(100% - 16px)' : 'auto',
            maxWidth: isMobile ? '400px' : '300px',
            left: isMobile ? '8px' : 'auto',
            right: isMobile ? '8px' : 'auto',
            top: isMobile ? '16px' : 'auto',
          }
        }
      );
    };

    return () => {
      if (unsubscribe) {
        unsubscribe(); // Clean up onMessage listener
      }
      // document.removeEventListener('visibilitychange', handleVisibilityChange);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, []);


  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <CustomThemeProvider>
        <RestaurantProvider>
          <AuthProvider>
            <NotificationProvider>
              <Router>
                <StyledToastContainer
                  position="bottom-right"
                  limit={5}
                  autoClose={5000}
                  pauseOnHover={false}
                  pauseOnFocusLoss={false}
                />
                <NavigationGuardWrapper>
                  <Routes>
                    <Route
                      path="/order/details/:uniqueString"
                      element={
                        <PageWrapper fallback={<CheckoutPageSkeleton />}>
                          <OrderDetailsPage />
                        </PageWrapper>
                      }
                    />
                    <Route path="/table-order" element={<TableOrderPage />} />
                    <Route
                      path="/table-order/:uniqueString"
                      element={<TableOrderPage />}
                    />
                    <Route path="/" element={<Navigate to="/delivery" replace />} />
                    <Route path="/home" element={<HeroSection />} />
                    <Route path="/guest/:guestId/:orderType/:orderId" element={<HeroSection />} />
                    <Route element={<UserNavbar />}>
                      <Route
                        path="/delivery"
                        element={
                          <PageWrapper fallback={<DeliveryPageSkeleton />}>
                            <DeliveryPage2 />
                          </PageWrapper>
                        }
                      />
                      {/* <Route path="/takeaway" element={
                      <PageWrapper fallback={<MenuItemSkeleton/>}>
                        <TakeawayPage />
                      </PageWrapper>
                    } /> */}
                      <Route
                        path="/catering"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <CateringPage />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="/order-success"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <OrderSuccessPage />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <SuperUserProtectedRoute>
                            <PageWrapper fallback={<MenuItemSkeleton />}>
                              <ProfilePage />
                            </PageWrapper>
                          </SuperUserProtectedRoute>
                        }
                      />

                      <Route path="/test" element={<TestPage />} />
                      {/* <Route path="/table-order" element={<TableOrderPage />} />
                      <Route
                        path="/table-order/:uniqueString"
                        element={<TableOrderPage />}
                      /> */}
                      <Route
                        path="/table-menu/:uniqueString"
                        element={<TableMenuPage />}
                      />
                      <Route
                        path="/table-checkout/:uniqueString"
                        element={
                          <PageWrapper>
                            <TableOrderCheckout />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="/checkout"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <CheckoutPage />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="/guest-signup"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <GuestSignupPage />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="/payment/success"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <PaymentResultPage />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="/payment/failed"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <PaymentResultPage />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="/payment/pending"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <PaymentResultPage />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="/outlets"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <PublicRestaurant />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="/table-reservations"
                        element={
                          <PageWrapper fallback={<TableReservationsSkeleton />}>
                            <TableReservationsPage />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="/privacy-page"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <PrivacyPolicyPage />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="/privacy"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <PrivacyPage />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="/about"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <AboutPage />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="/contact"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <ContactPage />
                          </PageWrapper>
                        }
                      />
                    </Route>

                    {/* Admin routes */}
                    <Route path="/admin/login" element={<Login />} />
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute>
                          <Layout notificationCount={notificationCount || 0} setNotificationCount={setNotificationCount} deliveryNotificationCount={deliveryNotificationCount} setDeliveryNotificationCount={setDeliveryNotificationCount} takeawayNotificationCount={takeawayNotificationCount} setTakeawayNotificationCount={setTakeawayNotificationCount} cateringNotificationCount={cateringNotificationCount} setCateringNotificationCount={setCateringNotificationCount} reservationNotificationCount={reservationNotificationCount} setReservationNotificationCount={setReservationNotificationCount} />
                        </ProtectedRoute>
                      }
                    >
                      {/* <Route element={<Header />}> */}
                      <Route
                        index
                        element={<Navigate to="/admin/dashboard" replace />}
                      />
                      <Route
                        path="dashboard"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <Dashboard />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="config"
                        element={
                          <PageWrapper fallback={<PageSkeleton />}>
                            <RestaurantConfig />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="social-links"
                        element={
                          <PageWrapper fallback={<PageSkeleton />}>
                            <SocialLinksConfig />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="outlets"
                        element={
                          <PageWrapper fallback={<PageSkeleton />}>
                            <OutletManagement />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="menu"
                        element={
                          <PageWrapper fallback={<PageSkeleton />}>
                            <MenuManagement />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="delivery"
                        element={
                          <PageWrapper fallback={<PageSkeleton />}>
                            <DeliveryManagement />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="catering"
                        element={
                          <PageWrapper fallback={<PageSkeleton />}>
                            <CateringManagement />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="info"
                        element={
                          <PageWrapper fallback={<PageSkeleton />}>
                            <CateringInfoManagement />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="quotation"
                        element={
                          <PageWrapper fallback={<PageSkeleton />}>
                            <QuotationComponent />
                          </PageWrapper>
                        }
                      />

                      <Route
                        path="invoice-catering"
                        element={
                          <PageWrapper fallback={<PageSkeleton />}>
                            <CateringInvoiceComponent />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="tokens"
                        element={
                          <PageWrapper fallback={<PageSkeleton />}>
                            <TokenManagement />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="tables"
                        element={
                          <PageWrapper fallback={<PageSkeleton />}>
                            <TableManagement />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="table-addition"
                        element={
                          <PageWrapper fallback={<PageSkeleton />}>
                            <TableAdditionManagement tableIdForCloseBill={tableIdForCloseBill} setTableIdForCloseBill={setTableIdForCloseBill} />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="table-orders"
                        element={
                          <PageWrapper fallback={<PageSkeleton />}>
                            <TableOrderManagement />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="testimonials"
                        element={
                          <PageWrapper fallback={<PageSkeleton />}>
                            <TestimonialManagement />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="notifications"
                        element={
                          <PageWrapper fallback={<PageSkeleton />}>
                            <NotificationManagement />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="users"
                        element={
                          <PageWrapper fallback={<PageSkeleton />}>
                            <UserManagement />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="user-settings"
                        element={
                          <PageWrapper fallback={<PageSkeleton />}>
                            <UserSettingsManagment />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="printer-settings"
                        element={
                          <PageWrapper fallback={<PageSkeleton />}>
                            <PrinterSettingsManagement />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="analytics"
                        element={
                          <PageWrapper fallback={<SkeletonLoader />}>
                            {/* <CircularProgress /> */}
                            <PageSkeleton />
                            {/* // <LoadingFallback/> */}
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="create-quotation"
                        element={
                          <PageWrapper fallback={<SkeletonLoader />}>
                            <Quotation />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="sales-report"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <SalesReportManagement />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="orders-report"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <OrderReportManagement />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="performance-report"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <MenuPerformanceReportManagement />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="gst-report"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <GSTReportManagement />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="peak-report"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <PeakHoursItemsManagement />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="peakorders-report"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <PeakHoursOrdersManagement />
                          </PageWrapper>
                        }
                      />
                      <Route
                        path="feedback"
                        element={
                          <PageWrapper fallback={<DashboardSkeleton />}>
                            <FeedbackManagement />
                          </PageWrapper>
                        }
                      />
                    </Route>

                    {/* </Route> */}

                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </NavigationGuardWrapper>
              </Router>
            </NotificationProvider>
          </AuthProvider>
        </RestaurantProvider>
      </CustomThemeProvider>
    </LocalizationProvider>
  );
}

export default App;
